/**
 * Nav + page icons, v3 style (Material Symbols via @mui/icons-material).
 *
 * Material Symbols are the project-wide icon standard (see
 * CLAUDE.md: "Always use Material-UI icons"). Every icon here
 * renders via `@mui/icons-material`, which emits SVGs that inherit
 * `currentColor` through `fill="currentColor"` on the root — so
 * they automatically re-tint for dark mode, hover, and active nav
 * states without extra CSS.
 *
 * The API is kept backwards-compatible with the legacy *Logo.jsx
 * icons so the route files in `@app/router/constants/privateRoutes`
 * can swap one import for another without further changes:
 *
 *   - `fill` prop from legacy call sites is accepted and forwarded
 *     to the SVG, supplementing `currentColor`.
 *   - `className` prop is forwarded to the svg element.
 *   - `size` prop (default 18) maps to the fontSize-derived 1em size.
 *
 * Usage in routes:
 *
 *   import { PatientsIcon } from '@shared/ui/icons';
 *   // ...
 *   icon: PatientsIcon,
 *
 * shared/ui is the only layer allowed to import `@mui/icons-material`
 * directly (see .eslintrc GROUP 1 exemption).
 */
import StethoscopeIcon from '@mui/icons-material/MedicalServicesOutlined';
import ManageAccountsIcon from '@mui/icons-material/ManageAccountsOutlined';
import AssignmentIndIcon from '@mui/icons-material/AssignmentIndOutlined';
import DescriptionIcon from '@mui/icons-material/DescriptionOutlined';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooksOutlined';
import DashboardIconMui from '@mui/icons-material/SpaceDashboardOutlined';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeartOutlined';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActiveOutlined';
import GroupsIcon from '@mui/icons-material/GroupsOutlined';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import FolderOpenIcon from '@mui/icons-material/FolderOpenOutlined';
import Inventory2Icon from '@mui/icons-material/Inventory2Outlined';
import TrackChangesIcon from '@mui/icons-material/TrackChangesOutlined';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLongOutlined';
import BarChartIcon from '@mui/icons-material/BarChartOutlined';
import FlagIcon from '@mui/icons-material/FlagOutlined';
import SmartToyIcon from '@mui/icons-material/SmartToyOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesomeOutlined';
import InsightsIcon from '@mui/icons-material/InsightsOutlined';
import PaidIcon from '@mui/icons-material/PaidOutlined';
import RouteIcon from '@mui/icons-material/RouteOutlined';
import TuneIcon from '@mui/icons-material/TuneOutlined';

const DEFAULT_SIZE = 18;

const wrap = (Icon, displayName) => {
  const Wrapped = ({ size = DEFAULT_SIZE, className, style, fill, ...rest }) => (
    <Icon
      className={className}
      style={{
        fontSize: size,
        width: size,
        height: size,
        // Legacy call sites sometimes pass `fill="currentColor"`
        // (from the Sidebar) — forward that onto the svg color so
        // the icon tints with the parent <li>. When no fill is
        // passed, Material Symbols already use currentColor.
        color: fill || 'currentColor',
        ...style,
      }}
      {...rest}
    />
  );
  Wrapped.displayName = `NavIcon(${displayName})`;
  return Wrapped;
};

// Organization
export const AccountsIcon = wrap(StethoscopeIcon, 'Accounts');
export const UsersIcon = wrap(ManageAccountsIcon, 'Users');
export const AllergiEndDocsIcon = wrap(DescriptionIcon, 'AllergiEndDocs');
export const QhslabDocsIcon = wrap(LibraryBooksIcon, 'QhslabDocs');
// Dev lineage used the Material AssignmentInd glyph for the Providers
// nav item — same symbol here, via the outlined @mui/icons-material set.
export const ProvidersIcon = wrap(AssignmentIndIcon, 'Providers');

// Monitoring
export const DashboardIcon = wrap(DashboardIconMui, 'Dashboard');
export const PopulationHealthIcon = wrap(MonitorHeartIcon, 'PopulationHealth');
export const AssessmentPerformanceIcon = wrap(InsightsIcon, 'AssessmentPerformance');
export const ClaimsManagerIcon = wrap(PaidIcon, 'ClaimsManager');
export const AlertsIcon = wrap(NotificationsActiveIcon, 'Alerts');

// Patient care
export const PatientsIcon = wrap(GroupsIcon, 'Patients');
export const AssessmentsIcon = wrap(AssignmentTurnedInIcon, 'Assessments');
export const ResourcesIcon = wrap(FolderOpenIcon, 'Resources');
export const OrdersIcon = wrap(Inventory2Icon, 'Orders');
export const ProgramsIcon = wrap(TrackChangesIcon, 'Programs');
export const JourneysIcon = wrap(RouteIcon, 'Journeys');

// Operations
export const ClaimsIcon = wrap(ReceiptLongIcon, 'Claims');
export const ClaimReportsIcon = wrap(BarChartIcon, 'ClaimReports');
export const ReportablesIcon = wrap(FlagIcon, 'Reportables');
export const BotActivityIcon = wrap(SmartToyIcon, 'BotActivity');
export const PermissionsTestingIcon = wrap(TuneIcon, 'PermissionsTesting');

// Instant assessment (currently disabled feature — keep for parity)
export const InstantAssessmentIcon = wrap(AutoAwesomeIcon, 'InstantAssessment');
